import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import config from '../../Common/Configurations/APIConfig';
import apiService from "../../Common/Configurations/APIService";
import Header from '../../Common/Layout/Header';
import { useAuth } from '../../Common/Configurations/AuthContext';
import plans from "./Plans.json";
import SubscriptionSuccessModal from '../Master/SubscriptionSuccessModal';
import SwitchPlanModal from '../Master/SwitchPlanModal';
import DefaultData from '../../CompanyData/DefaultData';

const getPlanBaseName = (planEnum) => {
  switch (planEnum) {
    case 1: case 4: return 'Starter';
    case 2: case 5: return 'Pro';
    case 3: case 6: return 'Enterprise';
    default: return '';
  }
};

const SubscriptionPlan = () => {
  const { user, loading, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState(1);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [purchasedPlanName, setPurchasedPlanName] = useState('');

  useEffect(() => {
    const getCurrentSubscription = async () => {
      if (!user?.cid) return;
      try {
        const resourceUrl = `${config.GETSUBSCRIPTIONBYCOMPANY}?companyId=${user.cid}`;
        const response = await apiService.getAll(resourceUrl);
        if (response && response.subscriptionId) {
          setCurrentSubscription(response);
        }
      } catch (error) {
        console.error("Could not fetch current subscription:", error);
      } finally {
        setDataLoading(false);
      }
    };
    if (!loading && user) {
      if (user.planType === 'TRIAL') setDataLoading(false);
      else getCurrentSubscription();
    }
  }, [user, loading]);

  const handleToggle = () => setBillingCycle(prev => (prev === 1 ? 2 : 1));

  const handlePlan = async (e, planId) => {
    e.preventDefault();
    if (user.planType !== 'TRIAL') { setShowSwitchModal(true); return; }
    setLoadingPlanId(planId);
    let backendPlanId;
    if (billingCycle === 1) {
      if (planId === 1) backendPlanId = 1; else if (planId === 2) backendPlanId = 2; else if (planId === 3) backendPlanId = 3;
    } else {
      if (planId === 1) backendPlanId = 4; else if (planId === 2) backendPlanId = 5; else if (planId === 3) backendPlanId = 6;
    }
    function loadScript(src) { return new Promise((resolve) => { const script = document.createElement("script"); script.src = src; script.onload = () => resolve(true); script.onerror = () => resolve(false); document.body.appendChild(script); }); }
    try {
      const response = await fetch(DefaultData.APIACTIVEURL + config.CREATESUBSCRIPTION, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyId: user?.cid, plan: backendPlanId, billingCycle, autoRenew: true, currency: "INR", paymentType: DefaultData.APPLICATIONTYPE }),
      });
      const data = await response.json();
      if (data.status) {
        const res = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
        if (!res) { alert("Razorpay SDK failed to load."); setLoadingPlanId(null); return; }
        const options = {
          key: DefaultData.DEFAULTRKEY, subscription_id: data.razorpaySubscriptionId, name: "Zotrai Travel Subscription", description: `${plans.find(p => p.id === planId).name} Plan`, prefill: { name: user?.brandName, email: user?.email },
          theme: { color: "#2563EB" },
          handler: async function (response) {
            setLoadingPlanId(null); const confirmResponse = await fetch(`${DefaultData.APIACTIVEURL}${config.CONFIRMSUBSCRIPTION}?SubscriptionId=${data.razorpaySubscriptionId}`, { method: "POST", headers: { "Content-Type": "application/json" } });
            if (confirmResponse.ok) { await refreshUserProfile(); const planDetails = plans.find(p => p.id === planId); setPurchasedPlanName(planDetails.name); setShowSuccessModal(true); let count = 5; const interval = setInterval(() => { count--; setCountdown(count); if (count === 0) { clearInterval(interval); navigate('/dashboard'); } }, 1000); }
          },
          modal: { ondismiss: function () { setLoadingPlanId(null); } }
        };
        const rzp = new window.Razorpay(options); rzp.open();
      } else { alert(data.message); setLoadingPlanId(null); }
    } catch (err) { console.error("Error creating subscription:", err); setLoadingPlanId(null); }
  };

  if (loading || dataLoading) return <div id="layout-wrapper"><Header /><p className="text-center py-5">Loading plans...</p></div>;
  if (!user) { navigate("/"); return null; };

  // Helper to get accent color based on plan
  const getAccentColor = (planName) => {
    if (planName === 'Starter') return '#10B981'; // Green
    if (planName === 'Pro') return '#3B82F6';    // Blue
    return '#8B5CF6'; // Purple for Enterprise
  };

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        {/* ENHANCEMENT 1: Gradient Background */}
        <div className="page-content" style={{
          background: 'linear-gradient(180deg, #F8F9FC 0%, #E9EFF6 100%)',
          minHeight: '100vh'
        }}>
          <div className="container py-2">

            <div className="text-center mb-2">
              <h1 className="fw-bold text-dark mb-2">Plans & Pricing</h1>
              <p className="text-muted fs-5">Pick the plan that's right for your business growth</p>

              <div className="d-inline-flex bg-white p-1 rounded-pill shadow-sm border mt-2">
                <button
                  className={`btn rounded-pill px-4 py-2 fw-semibold ${billingCycle === 1 ? 'btn-primary shadow-sm' : 'btn-link text-muted'}`}
                  onClick={() => setBillingCycle(1)}
                  style={{ textDecoration: 'none' }}
                >
                  Monthly
                </button>
                <button
                  className={`btn rounded-pill px-4 py-2 fw-semibold ${billingCycle === 2 ? 'btn-primary shadow-sm' : 'btn-link text-muted'}`}
                  onClick={() => setBillingCycle(2)}
                  style={{ textDecoration: 'none' }}
                >
                  Annually <span className="ms-1 badge bg-warning text-dark" style={{ fontSize: '10px' }}>Save 20%</span>
                </button>
              </div>
            </div>

            <div className="row justify-content-center mt-4">
              {plans.map((plan) => {
                const isMonthly = billingCycle === 1;
                const isCurrentPlan = currentSubscription?.billingCycle === billingCycle && getPlanBaseName(currentSubscription?.plan) === plan.name;
                const isThisPlanLoading = loadingPlanId === plan.id;
                const accentColor = getAccentColor(plan.name);

                return (
                  <div key={plan.id} className="col-12 mb-5">
                    {/* ENHANCEMENT 2: Colorful top border accent */}
                    <div
                      className="card shadow-lg overflow-hidden"
                      style={{
                        borderRadius: '20px',
                        border: `2px solid ${accentColor}`,
                        borderTop: `6px solid ${accentColor}`,
                        transition: 'box-shadow 0.3s ease'
                      }}
                    >

                      {plan.popular && (
                        <div className="position-absolute top-0 end-0 bg-primary text-white px-4 py-2 fw-bold" style={{ borderBottomLeftRadius: '20px', zIndex: 10 }}>
                          ⭐ MOST POPULAR
                        </div>
                      )}

                      <div className="row g-0 align-items-stretch">

                        {/* ENHANCEMENT 3: Subtle color tint for the left section of popular plan */}
                        <div className={`col-lg-4 p-5 text-center border-end d-flex flex-column justify-content-center`}
                          style={{ backgroundColor: plan.popular ? '#F4F9FF' : '#ffffff' }}>
                          <div className="mb-2 fs-1">{plan.emoji}</div>
                          <h3 className="fw-bold text-dark mb-2">{plan.name}</h3>
                          <p className="text-muted small px-3 mb-4">{plan.description}</p>

                          <div className="mb-4">
                            <span className="display-5 fw-bold text me-1">INR</span>
                            <span className="display-5 fw-bold text-dark">{isMonthly ? plan.monthly : plan.annually}</span>
                            <div className="text-muted small fw-medium mt-n1">per {isMonthly ? "month" : "year"}</div>
                          </div>

                          {isCurrentPlan ? (
                            <button className="btn btn-success btn-lg w-100 rounded-pill fw-bold py-3 disabled shadow-sm">
                              <i className="ri-checkbox-circle-line me-2"></i>Current Plan
                            </button>
                          ) : (
                            <button
                              onClick={(e) => handlePlan(e, plan.id)}
                              className="btn btn-lg w-100 rounded-pill fw-bold py-3 shadow"
                              style={{ backgroundColor: accentColor, color: '#fff', border: 'none' }}
                              disabled={loadingPlanId !== null}
                            >
                              {isThisPlanLoading ? (
                                <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</>
                              ) : (
                                <>{user.planType === 'TRIAL' ? 'Get Started' : 'Upgrade Now'} <i className="ri-arrow-right-line ms-2"></i></>
                              )}
                            </button>
                          )}
                        </div>

                        <div className="col-lg-8 p-3" style={{ backgroundColor: '#FBFDFF' }}>
                          <h6 className="text-uppercase fw-bold mb-4 small ls-1" style={{ color: accentColor }}>Included in {plan.name}</h6>

                          <div className="row g-4">
                            {plan.features.map((feature, i) => (
                              <div key={i} className="col-md-4 col-6">
                                <div className="d-flex align-items-start gap-2">
                                  {/* Icon background matches plan accent color slightly */}
                                  <div className="rounded-circle p-1 d-flex" style={{ backgroundColor: `${accentColor}15` }}>
                                    <i className="ri-check-line fw-bold" style={{ fontSize: '14px', color: accentColor }}></i>
                                  </div>
                                  <span className="text-dark small fw-medium">{feature}</span>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="mt-5 pt-4 border-top d-flex align-items-center justify-content-between text-muted small">
                            <span>Transparent pricing, no hidden fees.</span>
                            <a href="#" className="fw-semibold text-decoration-none" style={{ color: accentColor }}>Need help? Chat with us</a>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <SubscriptionSuccessModal show={showSuccessModal} planName={purchasedPlanName} countdown={countdown} />
      <SwitchPlanModal show={showSwitchModal} onHide={() => setShowSwitchModal(false)} />
    </div>
  );
};

export default SubscriptionPlan;